import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, X, Loader2, Combine } from 'lucide-react';
import {
  useMappingsList,
  useRebalanceRuns,
  useTaxonomyList,
  SECTOR_TAXONOMY_QK,
} from './useSectorTaxonomy';
import {
  REVIEW_SUBTABS,
  formatMappingStatus,
  formatTaxonomySource,
  type ReviewSubtabId,
} from './sectorTaxonomy.types';
import {
  approveMapping,
  mergeMappings,
  rejectMapping,
  type SectorMappingItem,
  type SectorTaxonomyNode,
} from '../../api/sectorTaxonomyApi';
import { InfoTooltip } from '../../components/InfoTooltip';
import { TX_TOOLTIPS } from './tooltipTexts';

interface Props {
  pushToast: (msg: string, tone?: 'info' | 'success' | 'error' | 'warn') => void;
}

// 탭 C — 리뷰 큐. 3 카테고리를 필터 칩으로 다중 선택:
//   C-1: Type-A 대기열 (rebalance/runs 목록)
//   C-2: Type-C 자동 감지 (pending_review + llm_confidence != null)
//   C-3: 미매핑 fallback (canonical_sector_id LIKE 'src_%')
//
// 아무 칩도 선택 안 된 상태가 default — 3개 섹션 모두 표시.
// 칩 클릭 토글: 선택된 카테고리만 표시.
export function ReviewQueueTab({ pushToast }: Props): JSX.Element {
  const [selected, setSelected] = useState<Set<ReviewSubtabId>>(new Set());

  const toggle = (id: ReviewSubtabId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 선택 없으면 전체 — 있으면 선택된 것만.
  const isVisible = (id: ReviewSubtabId) =>
    selected.size === 0 || selected.has(id);

  return (
    <div>
      <div className="tx-subtabs" role="tablist" aria-label="리뷰 큐 카테고리 필터">
        {REVIEW_SUBTABS.map((t) => {
          const active = selected.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`tx-subtab${active ? ' tx-subtab--active' : ''}`}
              onClick={() => toggle(t.id)}
            >
              {t.label}
            </button>
          );
        })}
        {selected.size > 0 && (
          <button
            type="button"
            className="pca-btn pca-btn--ghost"
            onClick={() => setSelected(new Set())}
            style={{ marginLeft: 8 }}
            aria-label="카테고리 필터 초기화"
          >
            초기화
          </button>
        )}
      </div>
      {isVisible('typeA') && (
        <section className="tx-review-section">
          {selected.size !== 1 && <h3 className="tx-review-section__title">Type-A 대기열</h3>}
          <TypeASection pushToast={pushToast} />
        </section>
      )}
      {isVisible('typeC') && (
        <section className="tx-review-section">
          {selected.size !== 1 && <h3 className="tx-review-section__title">Type-C 자동 감지</h3>}
          <TypeCSection pushToast={pushToast} />
        </section>
      )}
      {isVisible('unmapped') && (
        <section className="tx-review-section">
          {selected.size !== 1 && <h3 className="tx-review-section__title">미매핑 fallback</h3>}
          <UnmappedSection pushToast={pushToast} />
        </section>
      )}
    </div>
  );
}

// ── C-1: Type-A 대기열 ─────────────────────────────────────────
function TypeASection({ pushToast }: Props): JSX.Element {
  void pushToast;
  const runs = useRebalanceRuns(10);
  const items = runs.data ?? [];

  return (
    <div className="pca-table-wrap">
      {runs.isError && (
        <div className="pca-warning-banner">
          실행 이력 로드 실패: {(runs.error as Error).message}
        </div>
      )}
      <table className="pca-table">
        <thead>
          <tr>
            <th>run_id</th>
            <th>시작</th>
            <th>상태</th>
            <th>비용 (USD)</th>
            <th>제안</th>
            <th>미포괄</th>
            <th>모델</th>
            <th>trigger</th>
          </tr>
        </thead>
        <tbody>
          {runs.isLoading && (
            <tr>
              <td colSpan={8} className="pca-muted">
                로딩 중…
              </td>
            </tr>
          )}
          {!runs.isLoading && items.length === 0 && (
            <tr>
              <td colSpan={8} className="pca-muted">
                최근 Type-A 실행 이력이 없습니다.
              </td>
            </tr>
          )}
          {items.map((r) => (
            <tr key={r.runId}>
              <td>
                <span className="tx-tree__slug">{r.runId}</span>
              </td>
              <td>{r.startedAt}</td>
              <td>
                <span className="tx-status tx-status--confirmed">{r.status}</span>
              </td>
              <td>{r.costUsd !== null ? `$${r.costUsd.toFixed(4)}` : '—'}</td>
              <td>{r.proposedCount ?? '—'}</td>
              <td>{r.uncoveredCount ?? '—'}</td>
              <td className="pca-muted">{r.llmModel ?? '—'}</td>
              <td>{r.triggerBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── C-2: Type-C 자동 감지 ─────────────────────────────────────
function TypeCSection({ pushToast }: Props): JSX.Element {
  const qc = useQueryClient();
  const list = useMappingsList({
    page: 0,
    pageSize: 100,
    status: ['pending_review'],
    minConfidence: 0.01, // llm_confidence > 0 인 항목만 (Type-C 는 LLM 결과)
  });
  const items = list.data?.items ?? [];
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());

  // 머지 선택 상태 — 운영자가 다수 mapping 선택 후 단일 canonical 로 통합.
  // 다중선택은 mappingId 단위. 머지 모달에서 target canonical 지정.
  const [mergeSelected, setMergeSelected] = useState<Set<number>>(new Set());
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  const setBusy = useCallback((id: number, v: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (v) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: [SECTOR_TAXONOMY_QK.mappings] });
  }, [qc]);

  const toggleMergeSelect = useCallback((id: number) => {
    setMergeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectedItems = useMemo(
    () => items.filter((i) => mergeSelected.has(i.mappingId)),
    [items, mergeSelected],
  );

  async function handleApprove(item: SectorMappingItem): Promise<void> {
    setBusy(item.mappingId, true);
    try {
      await approveMapping(item.mappingId);
      pushToast(`mapping ${item.mappingId} 승인`, 'success');
      invalidate();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'approve_failed', 'error');
    } finally {
      setBusy(item.mappingId, false);
    }
  }

  async function handleReject(item: SectorMappingItem): Promise<void> {
    const reason = window.prompt('반려 사유를 입력하세요 (필수)') ?? '';
    if (!reason.trim()) return;
    setBusy(item.mappingId, true);
    try {
      await rejectMapping(item.mappingId, reason.trim());
      pushToast(`mapping ${item.mappingId} 반려`, 'success');
      invalidate();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'reject_failed', 'error');
    } finally {
      setBusy(item.mappingId, false);
    }
  }

  return (
    <div className="pca-table-wrap">
      {/* 머지 선택 툴바 — 1건 이상 선택 시 노출 */}
      {mergeSelected.size > 0 && (
        <div className="pca-filter" style={{ marginBottom: 8 }}>
          <span className="pca-muted">선택: {mergeSelected.size} 건</span>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            onClick={() => setMergeModalOpen(true)}
            aria-label="선택 항목 머지"
          >
            <Combine size={14} strokeWidth={1.75} />
            선택 항목 머지
          </button>
          <button
            type="button"
            className="pca-btn"
            onClick={() => setMergeSelected(new Set())}
          >
            선택 해제
          </button>
        </div>
      )}

      {list.isError && (
        <div className="pca-warning-banner">
          Type-C 목록 로드 실패: {(list.error as Error).message}
        </div>
      )}
      <table className="pca-table">
        <thead>
          <tr>
            <th style={{ width: 32 }}>
              <input
                type="checkbox"
                checked={items.length > 0 && items.every((i) => mergeSelected.has(i.mappingId))}
                onChange={(e) => {
                  if (e.target.checked) {
                    setMergeSelected(new Set(items.map((i) => i.mappingId)));
                  } else {
                    setMergeSelected(new Set());
                  }
                }}
                aria-label="머지 대상 전체 선택"
              />
            </th>
            <th>created</th>
            <th>source</th>
            <th>원천 섹터명</th>
            <th>→ 정규 섹터</th>
            <th>
              관계
              <InfoTooltip
                label={TX_TOOLTIPS.relation}
                testId="tx-tip-review-relation"
              />
            </th>
            <th>
              유사도
              <InfoTooltip
                label={TX_TOOLTIPS.jaccard}
                testId="tx-tip-review-jaccard"
              />
            </th>
            <th>confidence</th>
            <th>LLM 모델</th>
            <th>action</th>
          </tr>
        </thead>
        <tbody>
          {list.isLoading && (
            <tr>
              <td colSpan={10} className="pca-muted">
                로딩 중…
              </td>
            </tr>
          )}
          {!list.isLoading && items.length === 0 && (
            <tr>
              <td colSpan={10} className="pca-muted">
                대기 중인 Type-C 항목이 없습니다.
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.mappingId} className="tx-row--pending">
              <td>
                <input
                  type="checkbox"
                  checked={mergeSelected.has(item.mappingId)}
                  onChange={() => toggleMergeSelect(item.mappingId)}
                  aria-label={`merge select ${item.mappingId}`}
                />
              </td>
              <td className="pca-muted">{item.createdAt.slice(0, 16)}</td>
              <td>{formatTaxonomySource(item.source)}</td>
              <td>{item.sourceSectorName}</td>
              <td>
                <strong>{item.canonicalDisplayName}</strong>
                <br />
                <span className="tx-tree__slug">{item.canonicalSectorId}</span>
              </td>
              <td>
                {item.relation ? (
                  <span className="pca-muted">{item.relation}</span>
                ) : (
                  <span className="pca-muted">—</span>
                )}
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                {item.overlapRatio != null ? item.overlapRatio.toFixed(2) : '—'}
              </td>
              <td
                className={
                  (item.llmConfidence ?? 0) < 0.5
                    ? 'tx-conf--low'
                    : (item.llmConfidence ?? 0) >= 0.9
                      ? 'tx-conf--high'
                      : 'tx-conf--mid'
                }
              >
                {item.llmConfidence !== null
                  ? item.llmConfidence.toFixed(3)
                  : '—'}
              </td>
              <td className="pca-muted">{item.llmModel ?? '—'}</td>
              <td style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  className="pca-btn pca-btn--primary"
                  onClick={() => handleApprove(item)}
                  disabled={busyIds.has(item.mappingId)}
                >
                  {busyIds.has(item.mappingId) ? (
                    <Loader2 size={12} strokeWidth={1.75} className="pca-spin" />
                  ) : (
                    <Check size={12} strokeWidth={1.75} />
                  )}
                  승인
                </button>
                <button
                  type="button"
                  className="pca-btn"
                  onClick={() => handleReject(item)}
                  disabled={busyIds.has(item.mappingId)}
                >
                  <X size={12} strokeWidth={1.75} />
                  반려
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {mergeModalOpen && selectedItems.length > 0 && (
        <MergeModal
          items={selectedItems}
          onClose={() => setMergeModalOpen(false)}
          onSuccess={(resp) => {
            pushToast(
              `${resp.mergedCount} 건 → ${resp.targetCanonical.displayName} 머지 완료`,
              'success',
            );
            setMergeModalOpen(false);
            setMergeSelected(new Set());
            invalidate();
          }}
          onError={(m) => pushToast(m, 'error')}
        />
      )}
    </div>
  );
}

// ── 머지 모달 ───────────────────────────────────────────────────
// 선택된 mapping 목록 + target canonical 선택 + note 입력.
// target 은 활성 sector_taxonomy 중에서 검색·드롭다운.
interface MergeModalProps {
  items: SectorMappingItem[];
  onClose: () => void;
  onSuccess: (resp: { mergedCount: number; targetCanonical: { id: string; displayName: string }; affected: number[] }) => void;
  onError: (msg: string) => void;
}

function MergeModal({ items, onClose, onSuccess, onError }: MergeModalProps): JSX.Element {
  const taxonomy = useTaxonomyList({ isActive: true });
  const allCanonical: SectorTaxonomyNode[] = taxonomy.data ?? [];
  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCanonical.slice(0, 50);
    return allCanonical
      .filter(
        (n) =>
          n.displayName.toLowerCase().includes(q) ||
          n.canonicalSectorId.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [allCanonical, search]);

  async function handleSubmit(): Promise<void> {
    if (!targetId) {
      onError('target canonical 섹터를 선택하세요.');
      return;
    }
    setBusy(true);
    try {
      const resp = await mergeMappings({
        mappingIds: items.map((i) => i.mappingId),
        targetCanonicalSectorId: targetId,
        note: note.trim() || undefined,
      });
      onSuccess(resp);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'merge_failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tx-modal-backdrop" role="dialog" aria-modal="true">
      <div className="tx-modal" style={{ maxWidth: 640 }}>
        <h3 className="tx-modal__title">선택 매핑 머지</h3>
        <div className="tx-modal__body">
          선택된 <strong>{items.length}</strong> 개 매핑을 단일 정규 섹터로 통합합니다.
          target 이 아닌 원소들은 <code>rejected</code> 로 기록됩니다.
        </div>

        <div className="pca-table-wrap" style={{ maxHeight: 200, overflowY: 'auto' }}>
          <table className="pca-table">
            <thead>
              <tr>
                <th>mapping_id</th>
                <th>source</th>
                <th>원천 섹터명</th>
                <th>현 canonical</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.mappingId}>
                  <td>{it.mappingId}</td>
                  <td>{formatTaxonomySource(it.source)}</td>
                  <td>{it.sourceSectorName}</td>
                  <td>
                    <span className="tx-tree__slug">{it.canonicalSectorId}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <label
            htmlFor="tx-merge-search"
            style={{ display: 'block', fontSize: 12, marginBottom: 4 }}
          >
            target canonical 섹터 검색
          </label>
          <input
            id="tx-merge-search"
            className="pca-input"
            type="text"
            placeholder="이름 또는 slug (예: 반도체, sec_semiconductor)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', marginBottom: 6 }}
          />
          <select
            className="pca-input"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            style={{ width: '100%' }}
            size={Math.min(8, Math.max(3, filtered.length))}
          >
            <option value="">— 선택 —</option>
            {filtered.map((n) => (
              <option key={n.canonicalSectorId} value={n.canonicalSectorId}>
                {n.displayName} ({n.canonicalSectorId})
              </option>
            ))}
          </select>
          {taxonomy.isError && (
            <div className="pca-warning-banner" style={{ marginTop: 6 }}>
              택소노미 로드 실패 — 백엔드 상태 확인 필요.
            </div>
          )}
        </div>

        <div>
          <label htmlFor="tx-merge-note" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
            note (옵션)
          </label>
          <textarea
            id="tx-merge-note"
            className="pca-input"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="예: 동일 사업군 — 2026-04 운영자 결정"
            style={{ width: '100%' }}
          />
        </div>

        <div className="tx-modal__actions">
          <button type="button" className="pca-btn" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button
            type="button"
            className="pca-btn pca-btn--primary"
            onClick={handleSubmit}
            disabled={busy || !targetId}
          >
            {busy ? <Loader2 size={14} strokeWidth={1.75} className="pca-spin" /> : null}
            머지 실행
          </button>
        </div>
      </div>
    </div>
  );
}

// ── C-3: 미매핑 fallback (canonical_sector_id LIKE 'src_%') ──────
function UnmappedSection({ pushToast }: Props): JSX.Element {
  void pushToast;
  // 'src_' prefix 필터 — 백엔드 canonical_prefix 파라미터 경로.
  const list = useMappingsList({
    page: 0,
    pageSize: 200,
    canonicalPrefix: 'src_',
  });
  const items = list.data?.items ?? [];

  return (
    <div className="pca-table-wrap">
      <div className="pca-warning-banner" style={{ marginBottom: 8 }}>
        아래는 LLM 판정 전 임시로 <code>src_&lt;slug&gt;</code> 프리픽스로 생성된
        fallback 매핑입니다. “매핑 테이블” 탭에서 정규 섹터로 연결하거나, 새
        정규 섹터를 만들어 재매핑하세요.
      </div>
      {list.isError && (
        <div className="pca-warning-banner">
          로드 실패: {(list.error as Error).message}
        </div>
      )}
      <table className="pca-table">
        <thead>
          <tr>
            <th>mapping_id</th>
            <th>source</th>
            <th>원천 섹터명</th>
            <th>임시 canonical</th>
            <th>status</th>
            <th>created</th>
          </tr>
        </thead>
        <tbody>
          {list.isLoading && (
            <tr>
              <td colSpan={6} className="pca-muted">
                로딩 중…
              </td>
            </tr>
          )}
          {!list.isLoading && items.length === 0 && (
            <tr>
              <td colSpan={6} className="pca-muted">
                fallback 매핑이 없습니다.
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr key={item.mappingId}>
              <td>{item.mappingId}</td>
              <td>{formatTaxonomySource(item.source)}</td>
              <td>{item.sourceSectorName}</td>
              <td>
                <span className="tx-tree__slug">{item.canonicalSectorId}</span>
              </td>
              <td>
                <span className={`tx-status tx-status--${item.status}`}>
                  {formatMappingStatus(item.status)}
                </span>
              </td>
              <td className="pca-muted">{item.createdAt.slice(0, 16)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
